'use client';

import clsx from 'clsx';
import NextLink from "next/link";
import { memo } from 'react';
import AnimatedLink from "@/components/animated-link";
import Plus from "@/components/icon/plus";
import useBatchCardAnimation from '@/hooks/useBatchCardAnimation';


export default memo(function AddStoryCard({
  className,
  hasBackground = false,
  isParentInView,
  index = 0,
  isFirstInBatch = false,
  batchIndex,
  onBatchEnter,
  isHidden,
  isSmall = false,
  text,
  link,
}) {
  const hasAnimation = typeof onBatchEnter === 'function';
  const { rootRef, scrollRef } = useBatchCardAnimation({
    index,
    isParentInView,
    isFirstInBatch,
    batchIndex,
    onBatchEnter,
  });

  const buttonInner = (
    <div className="flex flex-col items-center justify-center gap-y-3.5 lg:gap-y-4.75">
      <div
        className="cta cta-icon-stroke-theme cta-light cta-circle-small lg:cta-circle group-hover:bg-main-green flex items-center justify-center gap-[0.625rem] transition-[background-color] duration-200"
        aria-hidden="true"
      >
        <Plus className="!w-5" />
      </div>
      <div
        className={clsx(
          "body-2-small xl:body-2 text-center text-pretty",
          !isSmall && "mx-auto max-w-[64%]",
        )}
        aria-hidden="true"
      >
        {text}
      </div>
    </div>
  );

  const buttonClassNames = clsx(
    "group border-dashed-long flex aspect-[146/196] cursor-pointer items-center justify-center hover:bg-main-light/15 transition-[background-color] duration-200",
    isSmall && "mx-3 w-[calc(100%-0)] lg:mx-4",
    !isSmall && "w-full",
    hasBackground && "bg-main-light/10",
    hasAnimation &&
      "opacity-[0.001] will-change-transform motion-reduce:translate-y-0 motion-reduce:opacity-100",
    className,
  );

  const button =
    link?.target === "_blank" ? (
      <NextLink
        ref={hasAnimation ? rootRef : undefined}
        className={buttonClassNames}
        aria-label="Find your story"
        href={link.href}
        target="_blank"
      >
        {buttonInner}
      </NextLink>
    ) : (
      <AnimatedLink
        ref={hasAnimation ? rootRef : undefined}
        className={buttonClassNames}
        aria-label="Find your story"
        transitionName="overlay-in"
        href={link.href}
      >
        {buttonInner}
      </AnimatedLink>
    );

  if (hasAnimation) {
    return <div ref={scrollRef} className={clsx(isHidden && 'invisible')}>{button}</div>;
  }

  return button;
});
